import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { ReactComponent as CloseIcon } from 'assets/icons/close16.svg'
import cx from 'classnames'
import Avatar from 'common/Avatar/Avatar'
import { Button as AlmanacButton } from 'components/Button'
import { Popover } from 'components/Popover/Popover'
import { useUserProfiles } from 'hooks/queries/users/useUserProfiles'
import { useOverlayHack } from 'hooks/useOverlayHack'
import { SelectionAttributes } from 'proseShared/plugins/MentionPrompt/MentionPrompt'
import { MentionPrompt, ObjectsToLoad } from 'proseShared/prompts/MentionPrompt/MentionPrompt'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useKey, useMeasure } from 'react-use'
import {
  SelectPropertyEventDetails,
  selectPropertyEventName,
} from 'screens/Finder/components/FileTree/events/selectProperty'
import { Profile } from 'types'
import { getInitials } from 'utils'
import Styles from './Person.module.scss'
import { ChevronDown } from 'blaze-ui/icons'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { Button } from 'blaze-ui/Button'

const PERSON_MIN_WIDTH = 240

export type PersonProps = {
  value: number | string | Array<number | string>
  onUpdate: (value: PersonProps['value']) => void
  readOnly?: boolean
  variant?: 'default' | 'table-person-picker' | 'filter'
  onEditingStateChange?: (isEditing: boolean) => void
  allowMultiple?: boolean
  onClick?: () => void
  wrapperClassName?: string
  disabled?: boolean
}

export type PersonHandles = {
  setEditing: (editingState: boolean) => void
}

const PersonItem = ({
  isEditing,
  person,
  showCloseIcon = false,
  onRemove,
}: {
  person: Profile
  showCloseIcon?: boolean
  isEditing: boolean
  onRemove: (id: number) => void
}) => {
  return (
    <div className={cx(Styles.person, { [Styles.editing]: isEditing })}>
      <div className={Styles.avatarWrap}>
        <Avatar label={getInitials(person.display_name)} size={20} url={person.avatar_url || undefined} />
      </div>
      <p className={Styles.displayName}>{person.display_name}</p>
      {showCloseIcon && (
        <AlmanacButton className={Styles.closeIconButton} onPress={() => onRemove(person.id)} variant="tertiary">
          <CloseIcon />
        </AlmanacButton>
      )}
    </div>
  )
}

interface OptionButtonProps extends Pick<ButtonProps, 'isDisabled'> {
  onPress: () => void
  readOnly: boolean
  selectedUserIds: number[]
  variant: 'default' | 'table-person-picker' | 'filter'
  onRemove: (id: number) => void
  isEditing: boolean
  selectedUserProfiles: Map<number, Profile>
}

const OptionButton = forwardRef<HTMLButtonElement, OptionButtonProps>(
  ({ onPress, isDisabled, readOnly, selectedUserIds, variant, onRemove, isEditing, selectedUserProfiles }, fRef) => {
    if (variant === 'table-person-picker') {
      return (
        <button
          ref={fRef}
          className={cx(Styles.selectedPeople, { [Styles.disabled]: isDisabled })}
          onClick={onPress}
          data-testid="person-names"
          disabled={readOnly || isDisabled}
        >
          {selectedUserIds.map((userId) => {
            const person = selectedUserProfiles.get(userId)
            if (!person) return null
            return <PersonItem key={person.id} person={person} onRemove={onRemove} isEditing={isEditing} />
          })}
        </button>
      )
    }
    return (
      <Button
        endIcon={selectedUserIds.length === 0 && ['filter', 'default'].includes(variant) ? ChevronDown : undefined}
        fullWidth
        ref={fRef}
        onPress={onPress}
        size="md"
        variant="tertiary"
      >
        {selectedUserIds.length === 0 && ['filter', 'default'].includes(variant) && (
          <span className={Styles.placeholder}>Add value</span>
        )}
        {selectedUserIds.map((userId) => {
          const person = selectedUserProfiles.get(userId)
          if (!person) return null
          return <PersonItem key={person.id} person={person} onRemove={onRemove} isEditing={isEditing} />
        })}
      </Button>
    )
  },
)

export const Person = forwardRef<PersonHandles, PersonProps>(
  (
    {
      value,
      onUpdate,
      readOnly = false,
      variant = 'default',
      onEditingStateChange,
      allowMultiple = true,
      onClick,
      wrapperClassName,
      disabled,
    },
    ref,
  ) => {
    const [query, setQuery] = useState('')
    const [isEditing, setIsEditing] = useState(false)
    const editablePeopleRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const selectedPeopleButtonRef = useRef<HTMLButtonElement>(null)
    const [inputSizerRef] = useMeasure<HTMLDivElement>()
    const [buttonRef, { height: buttonHeight, width: buttonWidth }] = useMeasure<HTMLButtonElement>()
    const selectedUserIds = useMemo(() => {
      if (Array.isArray(value)) {
        return value.map((v) => (typeof v === 'number' ? v : parseInt(v)))
      }
      return typeof value === 'number' ? [value] : [parseInt(value)]
    }, [value])
    const selectedUserProfiles = useUserProfiles(selectedUserIds)
    useEffect(() => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      buttonRef(selectedPeopleButtonRef.current!)
    }, [buttonRef, selectedPeopleButtonRef])

    const overlayRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      onEditingStateChange?.(isEditing)
      if (isEditing) {
        // Input will only be mounted (and thus focusable) after the next render
        setTimeout(() => {
          inputRef.current?.focus()
        }, 0)
      }
    }, [isEditing, onEditingStateChange])

    useKey(
      'Backspace',
      (e) => {
        if (query.length === 0 && e.target === inputRef.current && Array.isArray(value) && value.length > 0) {
          removeSelectedPerson(Number(value[value.length - 1]))
        }
      },
      undefined,
      [query, value],
    )

    useKey(
      'Tab',
      (e) => {
        if (
          variant !== 'table-person-picker' ||
          !(e.target === inputRef.current || overlayRef.current?.contains(e.target as Node))
        ) {
          return
        }

        setIsEditing(false)
        // closing the dropdown will focus its cell, so we queue the focus change event on the next tick
        setTimeout(() => {
          document.body.dispatchEvent(
            new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
              bubbles: false,
              detail: e.shiftKey ? 'Previous' : 'Next',
            }),
          )
        }, 0)
      },
      undefined,
      [variant, inputRef, overlayRef],
    )

    useImperativeHandle(ref, () => ({
      setEditing: (editingState) => {
        setIsEditing(editingState)
        if (editingState) {
          inputRef.current?.focus()
        }
      },
    }))

    const { shouldFlipOverlayPlacement, positionPropsStyle } = useOverlayHack(
      overlayRef,
      selectedPeopleButtonRef,
      isEditing,
      buttonHeight,
      14,
    )

    const handleEditablePeopleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === editablePeopleRef.current) {
        inputRef.current?.focus()
      }
    }

    const handleOnSelect = (user: SelectionAttributes) => {
      if (!user.userId) return
      if (allowMultiple) {
        if (Array.isArray(value)) {
          if (user.userId && value.find((v) => v?.toString() === user.userId?.toString())) {
            onUpdate(value.filter((v) => Number(v) !== Number(user.userId)))
            setQuery('')
            return
          }

          onUpdate([...value, user.userId])
          setQuery('')
          return
        }

        onUpdate([value, user.userId])
      } else {
        let newSelection = [user.userId]
        if (Array.isArray(value)) {
          if (user.userId && value.find((v) => v?.toString() === user.userId?.toString())) {
            newSelection = []
          }
        }
        onUpdate(newSelection)
        setQuery('')
        setIsEditing(false)
      }
    }

    const removeSelectedPerson = (id: number) => {
      if (Array.isArray(value)) {
        const updatedValue = value.filter((v) => Number(v) !== Number(id))
        onUpdate(updatedValue)
        return
      }

      if (Number(value) === Number(id)) {
        onUpdate([])
      }
    }

    const handleButtonClick = useCallback(() => {
      setIsEditing(!readOnly)
      onClick?.()
    }, [readOnly, onClick])

    return (
      <div
        className={cx(Styles.container, wrapperClassName, Styles[variant], {
          [Styles.disabledContainer]: variant === 'filter' && readOnly,
        })}
      >
        <OptionButton
          ref={selectedPeopleButtonRef}
          onPress={handleButtonClick}
          readOnly={readOnly}
          selectedUserIds={selectedUserIds}
          variant={variant}
          onRemove={removeSelectedPerson}
          isEditing={isEditing}
          selectedUserProfiles={selectedUserProfiles}
        />
        {isEditing && (
          <FloatingPortal id="floating-ui-root">
            <Popover
              style={{
                ...positionPropsStyle,
                bottom: (positionPropsStyle.bottom ?? 0) - (shouldFlipOverlayPlacement ? 4 : 0),
              }}
              isOpen={true}
              onClose={() => setIsEditing(false)}
              ref={overlayRef}
              isDismissable
              className={cx(Styles.popover, { [Styles.flipped]: shouldFlipOverlayPlacement })}
            >
              <div className={Styles.editablePeople} ref={editablePeopleRef} onClick={handleEditablePeopleClick}>
                {selectedUserIds.map((userId) => {
                  const person = selectedUserProfiles.get(userId)
                  if (!person) return null
                  return (
                    <PersonItem
                      key={person.id}
                      person={person}
                      showCloseIcon
                      isEditing={isEditing}
                      onRemove={removeSelectedPerson}
                    />
                  )
                })}
                <div className={Styles.sizer} ref={inputSizerRef} aria-hidden={true}>
                  {query}
                </div>
                <input
                  type="text"
                  className={Styles.input}
                  placeholder={selectedUserIds.length === 0 ? 'Search or select a person' : ''}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  ref={inputRef}
                  style={{ width: buttonWidth }}
                  autoFocus
                />
              </div>
              <MentionPrompt
                inputQuery={query}
                suggestRecentlyReadArticle={false}
                allowCreateNewFile={false}
                onSelect={handleOnSelect}
                dropdownWidth={`${Math.max(PERSON_MIN_WIDTH, buttonWidth + 4)}px`}
                objectsToLoad={[ObjectsToLoad.Users]}
              />
            </Popover>
          </FloatingPortal>
        )}
      </div>
    )
  },
)
Person.displayName = 'Person'
